import { useEffect, useState } from "react";
import useSubscriptions from "../../../../hooks/useSubscriptions";
import { useStoreWrapper } from "../../../../store/store";
import { Pencil, Trash2, CirclePlay, CirclePause } from "lucide-react";
import dayjs from "dayjs";
import CustomLoader from "../../../../components/CustomLoader";
import AlertDialog from "../../Users/components/AlertDialog/AlertDialog";
import { toast } from "react-toastify";
import Modalant from "../../../../components/ModalA/Modalant";
import EmailSubscriptionForm from "../../analytics/fall-analysis/form/EmailSubForm";
import { DeleteMessage } from "./SubModals";
import { modifyTitle } from "../../../../utils/helpers/analyticsUtils";

interface Subscription {
  Id: string;
  User__c: string;
  Report_Type__c: string;
  Start_Date__c: string;
  End_Date__c?: string;
  Status__c: string;
  Interval_Type__c: string;
  Description__c?: string;
  Day_Of_The_Week__c?: string;
  Day_Of_The_Month__c?: string;
  Facility__r?: {
    Name: string;
  };
  CreatedDate?: string;
}

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [dialogueInfo, setDialogueInfo] = useState<{
    subscription: any;
    action: "pause" | "delete";
    open: boolean;
  }>({
    subscription: null,
    action: "pause",
    open: false,
  });
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    string | null
  >(null);
  const [selectedSubscription, setSelectedSubscription] =
    useState<Subscription | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [mode, setMode] = useState<"edit" | "add">("add");

  const { user, userFacilities } = useStoreWrapper((state) => ({
    user: state.user,
    userFacilities: state.userFacilities,
  }));

  const {
    handleGetUserSubscriptions,
    handleDeleteSingleSubscription,
    handlePostSubscription,
  } = useSubscriptions({
    userId: user?.userId,
    subscriptionId: selectedSubscriptionId,
  });

  const {
    data: subscriptionsData,
    isLoading,
    refetch: refetchSubscriptions,
  } = handleGetUserSubscriptions;
  const { mutateAsync: deleteSubscription, isLoading: isDeletingSubscription } =
    handleDeleteSingleSubscription;

  const { isLoading: isLoadingSubscription, mutateAsync: doPostSubscription } =
    handlePostSubscription;

  useEffect(() => {
    if (subscriptionsData !== undefined) {
      setSubscriptions(subscriptionsData.data?.data);
    }
  }, [subscriptionsData]);

  const intervalTypes = ["Daily", "Weekly", "Monthly"];

  const groupedSubscriptions = subscriptions.reduce(
    (acc, sub) => {
      const key = sub.Interval_Type__c;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(sub);
      return acc;
    },
    {} as Record<string, Subscription[]>
  );

  const sortedGroupedSubscriptions = Object.fromEntries(
    Object.entries(groupedSubscriptions).sort(
      ([a], [b]) => intervalTypes.indexOf(a) - intervalTypes.indexOf(b)
    )
  );

  function handleEdit(subscription: any) {
    setSelectedSubscription(subscription);
    setMode("edit");
    setShowSubscriptionModal(true);
  }

  const handleDelete = async (subscriptionId: string) => {
    try {
      const res = await deleteSubscription(subscriptionId);
      toast.success(res.data.message);
      refetchSubscriptions();
    } catch (error: any) {
      console.log({ error });
      toast.error(error.response.data.message);
    }
    setDialogueInfo({
      ...dialogueInfo,
      open: false,
      subscription: null,
    });
    setSelectedSubscription(null);
  };

  function confirmDelete(subscriptionId: string) {
    setSelectedSubscriptionId(subscriptionId);
    setDialogueInfo({
      ...dialogueInfo,
      open: true,
      subscription: null,
    });
  }

  function handleDialogClose() {
    if (selectedSubscriptionId) {
      handleDelete(selectedSubscriptionId);
      setSelectedSubscription(null);
    } else {
      toast.error("No Subscription ID detected");
    }
    setSelectedSubscription(null);
  }

  const handleFormSubmit = async (data: any) => {
    let facilityData = null;

    if (mode === "edit") {
      if (data.Facility__c) {
        facilityData = { Facility__c: data.Facility__c };
      } else if (data.Facilities__c) {
        facilityData = { Facilities__c: data.Facilities__c };
      }
    }

    const subscription = {
      Report_Type__c: data.Report_Type__c,
      Incident_Type__c: data.Incident_Type__c,
      Start_Date__c: data.startDate,
      End_Date__c: data.endDate,
      Interval_Type__c: data.recurrence,
      Description__c: data.description,
      User__c: user.userId,
      ...(data.recurrence === "weekly" && {
        Day_Of_The_Week__c: data.weeklyDays,
      }),
      ...(data.recurrence === "monthly" && {
        Day_Of_The_Month__c: data.monthlyDay,
      }),
      ...(mode === "edit" && {
        Id: selectedSubscription?.Id,
      }),
      ...facilityData,
      Trailing_Period__c: data.periodType,
      Trailing_Period_Quantity__c: data.periodLength,
    };

    try {
      const res = await doPostSubscription({ subscription });
      toast.success(res.data.message);
      refetchSubscriptions();
      setShowSubscriptionModal(false);
    } catch (err: any) {
      console.log({ err });
      toast.error(err.message);
    }
  };

  const handlePauseSubscription = async (_subscription: any) => {
    const subscription = {
      ..._subscription,
      Status__c: _subscription.Status__c === "Active" ? "Paused" : "Active",
    };
    try {
      const res = await doPostSubscription({ subscription });
      toast.success(res.data.message);
      refetchSubscriptions();
      setDialogueInfo({
        ...dialogueInfo,
        open: false,
        subscription: null,
      });
    } catch (err) {
      console.log({ err });
    }
  };

  function buildFacilityMap() {
    const map: any = {};
    userFacilities.forEach((facility: any) => {
      // Lowercase the key for case-insensitive lookups
      map[facility.facilityId.toLowerCase()] = facility.facilityName;
    });
    return map;
  }

  function getFacilityNamesForSubscription(subscription: any) {
    // Collect all facility IDs in an array
    const facilityIds = [];
    const facilityMap = buildFacilityMap();

    // 1) If there's a single facility in Facility__c
    if (subscription.Facility__c) {
      facilityIds.push(subscription.Facility__c);
    } else if (subscription.Facilities__c) {
      // Split by either commas or semicolons
      // (You can adjust the regex/split if you only store them as commas, etc.)
      const multipleIds = subscription.Facilities__c.split(/[;,]/);
      multipleIds.forEach((id: any) => {
        if (id.trim()) {
          facilityIds.push(id.trim());
        }
      });
    }

    // Map each facility ID to its facility name
    const facilityNames = facilityIds.map((id) => {
      // Convert ID to lowercase, then look up in the map
      const name = facilityMap[id.toLowerCase()] || id; // fallback: if not found, just use the ID
      return name;
    });

    // Return as comma-separated string
    return facilityNames.join(", ");
  }

  return (
    <div className="bg-white w-full p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold text-gray-500">
          Subscriptions
        </h1>
      </div>

      {isLoading ? (
        <CustomLoader />
      ) : (
        <div className="space-y-6">
          {Object.keys(sortedGroupedSubscriptions).length === 0 ? (
            <div className="mt-10 text-center text-xl text-gray-400">
              No Subscriptions available
            </div>
          ) : (
            Object.entries(sortedGroupedSubscriptions).map(
              ([intervalType, subs]) => (
                <div key={intervalType}>
                  <h2 className="font-heading mb-4 rounded-md bg-purple-100 p-3 text-xl font-semibold">
                    {intervalType}
                  </h2>
                  <div className="space-y-4">
                    {subs
                      .sort((a, b) =>
                        dayjs(a.CreatedDate).isBefore(dayjs(b.CreatedDate))
                          ? 1
                          : -1
                      )
                      .map((sub) => (
                        <div
                          key={sub.Id}
                          className="relative flex flex-col items-start justify-between rounded-md bg-gray-50 p-2 sm:flex-row sm:items-center"
                        >
                          <div className="mb-4 sm:mb-0 md:w-full">
                            <h3
                              className="font-heading font-medium"
                              style={{ color: "#482952" }}
                            >
                              {modifyTitle(sub.Description__c) ??
                                "No Description Provided"}
                            </h3>
                            <h2
                              className="text-xs sm:text-sm"
                              style={{ color: "#635F5F" }}
                            >
                              {getFacilityNamesForSubscription(sub) ??
                                "No Facility Provided"}
                            </h2>
                          </div>

                          <div className="w-full flex-shrink-0 sm:mb-0 sm:w-auto sm:px-4">
                            <p className="text-sm text-gray-600">
                              {dayjs(sub.Start_Date__c).format("D MMM YYYY")} to{" "}
                              {sub.End_Date__c
                                ? dayjs(sub.End_Date__c).format("DD MMM YYYY")
                                : "Ongoing"}
                            </p>
                          </div>

                          <div className="flex w-full flex-shrink-0 flex-col items-end sm:w-auto md:ml-10">
                            <span
                              className={`mb-2 inline-block rounded-full px-2 py-1 text-xs text-white-main ${
                                sub.Status__c === "Active"
                                  ? "bg-green-500"
                                  : "bg-yellow-400 text-yellow-800"
                              }`}
                            >
                              {sub.Status__c}
                            </span>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEdit(sub)}
                                className="flex cursor-pointer items-center border-0 bg-transparent p-2 text-gray-600 hover:text-gray-800"
                              >
                                <Pencil className="h-4 w-4" />
                                <span className="ml-2">Edit</span>
                              </button>
                              <button
                                onClick={() => {
                                  setDialogueInfo({
                                    subscription: sub,
                                    action: "pause",
                                    open: true,
                                  });
                                }}
                                className="flex cursor-pointer items-center border-0 bg-transparent p-2 text-gray-600 hover:text-gray-800"
                              >
                                {sub.Status__c === "Active" ? (
                                  <>
                                    <CirclePause className="h-4 w-4" />
                                    <span className="ml-2">Pause</span>
                                  </>
                                ) : (
                                  <>
                                    <CirclePlay className="h-4 w-4" />
                                    <span className="ml-2">Resume</span>
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  confirmDelete(sub.Id);
                                  setSelectedSubscription(sub);
                                  setDialogueInfo({
                                    subscription: sub,
                                    action: "delete",
                                    open: true,
                                  });
                                }}
                                className="flex cursor-pointer items-center border-0 bg-transparent p-2 text-gray-600 hover:text-gray-800"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span className="ml-2">Delete</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )
            )
          )}
        </div>
      )}

      {dialogueInfo.open && (
        <AlertDialog
          loading={isDeletingSubscription}
          message={
            <DeleteMessage
              setIsDialogOpen={() =>
                setDialogueInfo({
                  ...dialogueInfo,
                  open: false,
                  subscription: null,
                })
              }
              handleDialogClose={handleDialogClose}
              handlePauseSubscription={handlePauseSubscription}
              selectedSubscription={dialogueInfo.subscription}
              loading={isDeletingSubscription || isLoadingSubscription}
              action={dialogueInfo.action}
            />
          }
        />
      )}

      {showSubscriptionModal && (
        <Modalant
          isLoading={isLoadingSubscription}
          className="items-center md:w-6/12"
          isOpen={showSubscriptionModal}
          isClose={() => {
            setShowSubscriptionModal(false);
            setSelectedSubscription(null);
          }}
        >
          <EmailSubscriptionForm
            handleSubmit={handleFormSubmit}
            mode={mode}
            selectedSubscription={selectedSubscription}
            handleClose={() => {
              setShowSubscriptionModal(false);
              setSelectedSubscription(null);
            }}
            isLoading={isLoadingSubscription}
            isFacility={false}
            title={`${mode === "add" ? "New" : "Edit"} Subscription`}
          />
        </Modalant>
      )}
    </div>
  );
};

export default Subscriptions;
